import React, { useState } from 'react';
import { MapPin, Search } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { countries } from '../data/mock';

const GlobalReachSection = () => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    message: ''
  });

  const handleCountryClick = (country) => {
    setSelectedCountry(country);
    setIsDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    toast.success(`Request submitted for ${selectedCountry.name}! We'll contact you soon.`);
    setIsDialogOpen(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      organization: '',
      message: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <section id="global-reach" className="py-24 bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
            Global Reach
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Serving 195 Countries Worldwide
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Click on any country flag below to request our services in your region. 
            We're committed to protecting children across every continent.
          </p>

          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search for a country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 border-2 border-gray-200 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Countries Grid */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
            {filteredCountries.map((country) => (
              <button
                key={country.code}
                onClick={() => handleCountryClick(country)}
                className="group flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-blue-50 transition-all duration-200 border-2 border-transparent hover:border-blue-300 hover:shadow-md"
                title={country.name}
              >
                <div className="text-5xl group-hover:scale-125 transition-transform">
                  {country.flag}
                </div>
                <span className="text-xs text-gray-600 group-hover:text-blue-600 font-medium text-center leading-tight">
                  {country.name}
                </span>
              </button>
            ))}
          </div>

          {filteredCountries.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No countries found matching "{searchQuery}"</p>
            </div>
          )}

          <div className="mt-8 pt-8 border-t border-gray-200 text-center text-gray-600">
            <p className="text-sm">
              Don't see your country listed? We serve all 195 countries. 
              <button 
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-blue-600 hover:text-blue-700 font-semibold ml-1"
              >
                Contact us directly →
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Country Request Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-2xl">
              <span className="text-4xl">{selectedCountry?.flag}</span>
              Request Services in {selectedCountry?.name}
            </DialogTitle>
            <DialogDescription>
              Fill out the form below and our team will reach out to discuss how we can 
              support your child advocacy needs in {selectedCountry?.name}.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="John Doe"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="john@example.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1 234 567 8900"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="organization">Organization</Label>
              <Input
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleInputChange}
                placeholder="Your organization name"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                placeholder="Tell us about your needs and how we can help..."
                className="mt-1 min-h-[100px]"
              />
            </div>

            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base font-semibold">
              Submit Request
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default GlobalReachSection;
